import React, { useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { alpha, useTheme, lighten, darken } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import TimerIcon from "@mui/icons-material/Timer";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useClientTagAnalytics } from "../../hooks/useClientTagAnalytics";
import DashboardPieChartView from "../../components/dashboard/DashboardPieChartView";
import DashboardRankedBarChart from "../../components/dashboard/DashboardRankedBarChart";
import DashboardChannelCompareSection from "../../components/dashboard/DashboardChannelCompareSection";

const BR = (x) => (Number.isFinite(x) ? x : Number(x ?? 0)).toLocaleString("pt-BR");

const fieldSx = { minWidth: { xs: "100%", sm: 180 }, flex: { xs: "1 1 100%", sm: "1 1 180px" } };

const cardSx = {
  border: 1,
  borderColor: "divider",
  boxShadow: (th) =>
    th.palette.mode === "dark"
      ? `0 1px 4px ${alpha("#000", 0.35)}`
      : `0 1px 3px ${alpha(th.palette.common.black, 0.08)}`,
};

const secToHuman = (sec) => {
  if (sec == null || !Number.isFinite(Number(sec))) return "—";
  const s = Math.max(0, Math.round(Number(sec || 0)));
  if (!s) return "0m";
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d${h ? ` ${h}h` : ""}`;
  if (h > 0) return `${h}h${m ? ` ${m}m` : ""}`;
  return `${m || 1}m`;
};

const buildSeriesFromTheme = (theme) => {
  const p = theme.palette;
  return [
    p.primary.main,
    p.secondary?.main ?? p.info.main,
    p.warning.main,
    p.success.main,
    p.error.main,
    p.info.main,
    darken(p.primary.main, 0.2),
    lighten(p.primary.main, 0.35),
  ];
};

function KpiCard({ loading, icon, title, value, color, sub }) {
  if (loading) {
    return (
      <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
        <Skeleton variant="rounded" height={118} sx={{ width: "100%", borderRadius: 1 }} />
      </Grid>
    );
  }
  return (
    <Grid item xs={12} sm={6} md={3} sx={{ display: "flex" }}>
      <Card elevation={1} sx={{ width: "100%", display: "flex", flexDirection: "column", ...cardSx }}>
        <CardContent sx={{ flex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: color,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" noWrap>
                {title}
              </Typography>
              <Typography variant="h5" fontWeight={700} noWrap>
                {value}
              </Typography>
              {sub ? (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {sub}
                </Typography>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}

function ChartCard({ title, hint, loading, empty, children, height = 280 }) {
  return (
    <Card elevation={1} sx={{ height: "100%", ...cardSx }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        {hint ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {hint}
          </Typography>
        ) : null}
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height }}>
            <CircularProgress size={28} />
          </Box>
        ) : empty ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height }}>
            <Typography variant="body2" color="text.secondary">
              Sem dados no período.
            </Typography>
          </Box>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

function mapPieY(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((d) => ({
      name: String(d.name || d.path || "—"),
      value: Number(d.y ?? d.value ?? 0),
    }))
    .filter((d) => d.value > 0);
}

function SolicitacoesPanel({ data, loading, series }) {
  const cards = data?.cards || {};
  const charts = data?.charts || {};
  const byStatus = cards.byStatus || {};
  const theme = useTheme();

  const serieTemporal = useMemo(
    () =>
      (Array.isArray(charts.serieTemporal) ? charts.serieTemporal : []).map((r) => ({
        day: r.day,
        value: Number(r.value || 0),
      })),
    [charts.serieTemporal]
  );
  const emProcessamento = useMemo(() => mapPieY(charts.emProcessamento), [charts.emProcessamento]);
  const finalizados = useMemo(() => mapPieY(charts.finalizados), [charts.finalizados]);
  const motivosTop = useMemo(
    () =>
      (Array.isArray(charts.motivosTop) ? charts.motivosTop : []).map((r) => ({
        path: String(r.path || "—"),
        value: Number(r.value || 0),
      })),
    [charts.motivosTop]
  );

  const statusRows = [
    { name: "Pendentes", value: Number(byStatus.pendentes || 0) },
    { name: "Em atendimento", value: Number(byStatus.emAtendimento || 0) },
    { name: "Contato futuro", value: Number(byStatus.contatoFuturo || 0) },
    { name: "Concluídas", value: Number(byStatus.concluidas || 0) },
    { name: "Perdidas", value: Number(byStatus.perdidas || 0) },
  ].filter((r) => r.value > 0);

  return (
    <Stack spacing={2}>
      <Grid container spacing={1.5}>
        <KpiCard
          loading={loading}
          icon={<SupportAgentIcon fontSize="small" />}
          title="Solicitações"
          value={BR(cards.totalAtendimentos)}
          color={theme.palette.primary.main}
        />
        <KpiCard
          loading={loading}
          icon={<GroupsIcon fontSize="small" />}
          title="Clientes distintos"
          value={BR(cards.clientesComContatoHumano)}
          color={theme.palette.info.main}
          sub="com solicitação marcada"
        />
        <KpiCard
          loading={loading}
          icon={<TimerIcon fontSize="small" />}
          title="TMA médio"
          value={secToHuman(cards.avgTMAsec)}
          color={theme.palette.warning.main}
        />
        <KpiCard
          loading={loading}
          icon={<AccessTimeIcon fontSize="small" />}
          title="TME médio"
          value={secToHuman(cards.avgTMEsec)}
          color={theme.palette.secondary?.main || theme.palette.success.main}
        />
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Volume no período"
            hint="Solicitações com esta tag por dia"
            loading={loading}
            empty={!serieTemporal.some((r) => r.value > 0)}
          >
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={serieTemporal}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} width={40} />
                <ChartTooltip formatter={(v) => BR(v)} />
                <Line
                  type="monotone"
                  dataKey="value"
                  name="Solicitações"
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Por status"
            loading={loading}
            empty={!statusRows.length}
          >
            <DashboardPieChartView data={statusRows} series={series} height={260} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Em processamento" loading={loading} empty={!emProcessamento.length}>
            <DashboardPieChartView data={emProcessamento} series={series} height={260} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Finalizados" loading={loading} empty={!finalizados.length}>
            <DashboardPieChartView data={finalizados} series={series} height={260} />
          </ChartCard>
        </Grid>
        <Grid item xs={12}>
          <ChartCard title="Top motivos" loading={loading} empty={!motivosTop.length} height={320}>
            <DashboardRankedBarChart
              data={motivosTop}
              categoryKey="path"
              valueKey="value"
              valueName="Solicitações"
              fill={theme.palette.primary.main}
              height={280}
              formatValue={BR}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12}>
          <DashboardChannelCompareSection
            loading={loading}
            channelCompare={charts.channelCompare}
            handlerCompare={charts.handlerCompare}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}

function ClientesPanel({ data, loading, series }) {
  const cards = data?.cards || {};
  const charts = data?.charts || {};
  const theme = useTheme();

  const awarenessDist = useMemo(
    () =>
      (Array.isArray(charts.awarenessDist) ? charts.awarenessDist : []).map((r) => ({
        name: String(r.name || "—"),
        value: Number(r.value || 0),
      })),
    [charts.awarenessDist]
  );
  const demographics = useMemo(
    () =>
      (Array.isArray(charts.demographics) ? charts.demographics : []).map((r) => ({
        name: String(r.category || r.name || "—"),
        value: Number(r.value || 0),
      })),
    [charts.demographics]
  );
  const genderDist = useMemo(() => mapPieY(charts.genderDist), [charts.genderDist]);
  const geography = useMemo(
    () =>
      (Array.isArray(charts.geography) ? charts.geography : []).map((r) => ({
        name: String(r.name || "—"),
        value: Number(r.value || 0),
      })),
    [charts.geography]
  );

  const churnPct = Number(cards.churnPct);
  const recencia = Number(cards.recenciaMediaDias);

  return (
    <Stack spacing={2}>
      <Grid container spacing={1.5}>
        <KpiCard
          loading={loading}
          icon={<PersonOutlineIcon fontSize="small" />}
          title="Clientes com a tag"
          value={BR(cards.totalClientes)}
          color={theme.palette.primary.main}
        />
        <KpiCard
          loading={loading}
          icon={<TrendingUpIcon fontSize="small" />}
          title="Novos no período"
          value={BR(cards.novosPeriodo)}
          color={theme.palette.success.main}
        />
        <KpiCard
          loading={loading}
          icon={<GroupsIcon fontSize="small" />}
          title="Ativos"
          value={BR(cards.ativosPeriodo)}
          color={theme.palette.info.main}
        />
        <KpiCard
          loading={loading}
          icon={<AccessTimeIcon fontSize="small" />}
          title="Recência média"
          value={Number.isFinite(recencia) ? `${BR(recencia)} d` : "—"}
          color={theme.palette.warning.main}
          sub={
            Number.isFinite(churnPct)
              ? `Churn 90d: ${churnPct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`
              : undefined
          }
        />
      </Grid>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <ChartCard title="Awareness" loading={loading} empty={!awarenessDist.some((r) => r.value > 0)}>
            <DashboardPieChartView data={awarenessDist} series={series} height={260} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Demografia (faixa etária)" loading={loading} empty={!demographics.some((r) => r.value > 0)}>
            <DashboardRankedBarChart
              data={demographics}
              categoryKey="name"
              valueKey="value"
              valueName="Clientes"
              fill={theme.palette.info.main}
              height={260}
              formatValue={BR}
            />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Gênero" loading={loading} empty={!genderDist.length}>
            <DashboardPieChartView data={genderDist} series={series} height={260} />
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ChartCard title="Geografia (UF)" loading={loading} empty={!geography.some((r) => r.value > 0)}>
            <DashboardRankedBarChart
              data={geography}
              categoryKey="name"
              valueKey="value"
              valueName="Clientes"
              fill={theme.palette.secondary?.main || theme.palette.primary.main}
              height={260}
              formatValue={BR}
            />
          </ChartCard>
        </Grid>
      </Grid>
    </Stack>
  );
}

const TagAnalyticsDialog = ({ open, onClose, tagId, tagName }) => {
  const theme = useTheme();
  const series = useMemo(() => buildSeriesFromTheme(theme), [theme]);
  const [start, setStart] = useState(() => dayjs().startOf("month"));
  const [end, setEnd] = useState(() => dayjs().endOf("month"));
  const [appliedStart, setAppliedStart] = useState(() => dayjs().startOf("month").format("YYYY-MM-DD"));
  const [appliedEnd, setAppliedEnd] = useState(() => dayjs().endOf("month").format("YYYY-MM-DD"));
  const [tab, setTab] = useState(0);

  const startStr = start?.isValid?.() ? start.format("YYYY-MM-DD") : "";
  const endStr = end?.isValid?.() ? end.format("YYYY-MM-DD") : "";
  const periodOk = Boolean(startStr && endStr && startStr <= endStr);

  const { data, isLoading, isFetching, error } = useClientTagAnalytics({
    id: tagId,
    start: appliedStart,
    end: appliedEnd,
    enabled: Boolean(open && tagId),
  });

  const loading = Boolean(isLoading || isFetching);
  const errMsg =
    (typeof error?.message === "string" && error.message) ||
    (error ? "Erro ao carregar analytics da tag." : "");

  const displayName = data?.tag?.nome || tagName || "—";

  const applyPeriod = () => {
    if (!periodOk) return;
    setAppliedStart(startStr);
    setAppliedEnd(endStr);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" scroll="paper">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" fontWeight={700}>
          Analytics da tag
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {displayName}
          {tagId != null ? ` · ID ${tagId}` : ""}
        </Typography>
        <IconButton
          aria-label="Fechar"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Métricas das solicitações e dos clientes vinculadas a esta tag no período selecionado.
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} useFlexGap flexWrap="wrap" alignItems="center">
              <DatePicker
                label="Início"
                value={start}
                onChange={(v) => v && setStart(v.startOf("day"))}
                slotProps={{ textField: { size: "small", sx: fieldSx } }}
              />
              <DatePicker
                label="Fim"
                value={end}
                onChange={(v) => v && setEnd(v.endOf("day"))}
                slotProps={{ textField: { size: "small", sx: fieldSx } }}
              />
              <Button
                variant="contained"
                onClick={applyPeriod}
                disabled={!periodOk || loading}
                sx={{ minWidth: 120 }}
              >
                Aplicar
              </Button>
            </Stack>

            {loading ? <LinearProgress /> : null}
            {errMsg ? <Alert severity="error">{errMsg}</Alert> : null}

            <Tabs
              value={tab}
              onChange={(_, next) => setTab(next)}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <Tab label="Solicitações" />
              <Tab label="Clientes" />
            </Tabs>

            {tab === 0 ? (
              <SolicitacoesPanel data={data?.solicitacoes} loading={loading && !data} series={series} />
            ) : (
              <ClientesPanel data={data?.clientes} loading={loading && !data} series={series} />
            )}
          </Stack>
        </LocalizationProvider>
      </DialogContent>
    </Dialog>
  );
};

export default TagAnalyticsDialog;
